'use client';

import { useEffect, useState } from 'react';
import { Button, Stack } from '@mui/material';
import { isFalsy } from 'utility-types';
import { getAppStoreById, getPosts } from '@/api/appstore.api';
import type { ListPageModel } from '@/interfaces/service/api.interface';
import type { AppStore, AppStoreDto, AppStorePostFilter, AppStorePostSortOption, Post } from '@/interfaces/service/appstore/appstore.interface';
import { useAppStoreStore } from '@/stores/appstore.store';
import { setRequestPostParams } from '@/services/appstore.service';
import { POST_PAGGE_SIZE } from '@/constants/appstore/post.constants';
import { getToken } from '@/utils/auth.util';
import { checkIsPreventedDisplyForWeek } from '@/utils/modal.util';
import EmptyCompnent from '@/components/EmptyComponent';
import FlexBox from '@/components/FlexBox';
import GoogleChannelRequestModal, { ALLOW_GOOGLE_MODAL_STORAGE } from '@/views/appstore/components/google/GoogleChannelRequestModal';
import type { GoogleMapCrawlingRequestStatus } from '@/views/appstore/components/google/GoogleChannelRequestStatusModal';
import GoogleChannelRequestStatusModal from '@/views/appstore/components/google/GoogleChannelRequestStatusModal';
import DashboardSectionContainer from '@/views/appstore/dashboard/components/DashboardSectionContainer';
import AppStorePostsBarChart from '@/views/appstore/dashboard/components/basecontents/AppStorePostsBarChart';
import LatestReviews from '@/views/appstore/dashboard/components/basecontents/LatestReviews';
import PostGroupButtons from '@/views/appstore/dashboard/components/basecontents/PostGroupButtons';
import PostsWordsSummaries from '@/views/appstore/dashboard/components/basecontents/PostsWordsSummaries';
import { DESKTOP_CONTENTS_MAX_WIDTH } from '@/app/stylesguides';

const BaseDashboardContents = ({ targetAppStore, isMobile }: { targetAppStore: AppStore; isMobile: boolean }) => {
  const [currentAppStore, setCurrentAppStore] = useState(targetAppStore);

  const [ableAppStoreAddGoogleMap, setAableAppStoreAddGoogleMap] = useState(true);
  const [showNoticeModal, setShowNoticeModal] = useState(false);

  const [showGoogleStatusModal, setShowGoogleStatusModal] = useState(false);
  const [showRequestGoogleButton, setShowRequestGoogleButton] = useState(true);

  const [googleRequestStatus, setGoogleRequestStatus] = useState<GoogleMapCrawlingRequestStatus>();

  const [checkGooGleCrawlingStatus, setCheckGooGleCrawlingStatus] = useState(false);

  const { appStores } = useAppStoreStore((state) => state);

  let polling;

  const checkGoogleChannel = () => {
    const token = getToken();
    if (isFalsy(token)) {
      setShowRequestGoogleButton(false);
      return;
    }

    if (!currentAppStore) {
      return;
    }

    const googleMapInAppStore = currentAppStore.channels.find((channel) => channel.channelId === 'GOOGLE_MAP');

    setShowRequestGoogleButton(isFalsy(googleMapInAppStore));

    if (googleMapInAppStore) {
      setShowRequestGoogleButton(false);
      return;
    }

    const storageName = `${ALLOW_GOOGLE_MODAL_STORAGE}_${currentAppStore.storeId}`;
    const allowGoogleModal = checkIsPreventedDisplyForWeek(storageName);

    const noticeAddGoogleMap = allowGoogleModal && currentAppStore.channels.findIndex((channel) => channel.channelId === 'GOOGLE_MAP') === -1;
    setShowNoticeModal(noticeAddGoogleMap);
    setAableAppStoreAddGoogleMap(noticeAddGoogleMap);
  };

  useEffect(checkGoogleChannel, []);

  useEffect(() => {
    const token = getToken();
    if (isFalsy(token)) {
      return;
    }

    if (!currentAppStore) {
      return;
    }

    let checkCount = 0;

    if (checkGooGleCrawlingStatus) {
      const googleMapChannel = currentAppStore.channels.find((channel) => channel.channelId === 'GOOGLE_MAP');

      if (isFalsy(googleMapChannel)) {
        const _googleMapChannel = appStores[currentAppStore.storeId]?.channels?.find((channel) => channel.channelId === 'GOOGLE_MAP');
        if (!isFalsy(_googleMapChannel)) {
          setCurrentAppStore(appStores[currentAppStore.storeId]);
        } else {
          setCheckGooGleCrawlingStatus(false);
          return;
        }
      }

      polling = setInterval(async () => {
        const baseFilter: AppStorePostFilter = {
          channelFilter: 'GOOGLE_MAP',
          replyFilter: 'ALL'
        };
        const baseSortOption: AppStorePostSortOption = 'LATEST';
        const params = setRequestPostParams(
          { ...currentAppStore, filter: baseFilter, sortOption: baseSortOption },
          {
            pageNo: 1,
            pageSize: POST_PAGGE_SIZE
          }
        );

        const result = await Promise.allSettled([getPosts(params), getAppStoreById(currentAppStore.storeId)]).then((responses) => {
          const res =
            responses[0].status === 'fulfilled'
              ? (responses[0]?.value as unknown as {
                  data: Post[];
                  sliceValue: ListPageModel;
                })
              : undefined;

          const latestAppStore = responses[1].status === 'fulfilled' ? (responses[1]?.value as unknown as AppStoreDto) : undefined;

          return {
            postsLength: res?.data?.length ?? 0,
            status: latestAppStore?.channels.find((channel) => channel.channelId === 'GOOGLE_MAP')?.status
          };
        });

        if (result) {
          if (result.postsLength > 0) {
            setGoogleRequestStatus('HAS_REVIEW');
            setShowGoogleStatusModal(true);
            setCheckGooGleCrawlingStatus(false);
            clearInterval(polling);
            return;
          } else if (result.status === 'SUCCESS') {
            setGoogleRequestStatus('NO_REVIEW');
            setShowGoogleStatusModal(true);
            setCheckGooGleCrawlingStatus(false);
            clearInterval(polling);
            return;
          } else if (result.status === 'FAIL') {
            setGoogleRequestStatus('FAIL');
            setShowGoogleStatusModal(true);
            setCheckGooGleCrawlingStatus(false);
            clearInterval(polling);
            return;
          }
        }

        if (checkCount > 5) {
          setGoogleRequestStatus('PROGRESS_NOT_END');
          setShowGoogleStatusModal(true);
          setCheckGooGleCrawlingStatus(false);
          clearInterval(polling);
        }

        checkCount++;
      }, 5000);
    }
    return () => clearInterval(polling);
  }, [checkGooGleCrawlingStatus]);

  return (
    <>
      {showRequestGoogleButton ? (
        <Button size="large" color="success" variant="contained" onClick={() => setShowNoticeModal(true)}>
          구글맵 리뷰 추가하기
        </Button>
      ) : (
        <EmptyCompnent />
      )}
      <PostGroupButtons currentAppStore={currentAppStore} isMobile={isMobile} isStable={currentAppStore.status === 'STABLE'} />
      <FlexBox
        style={{
          gap: '16px',
          width: '100%',
          maxWidth: `${DESKTOP_CONTENTS_MAX_WIDTH}px`,
          alignItems: 'stretch',
          flexWrap: 'wrap'
        }}
      >
        <DashboardSectionContainer isMobile={isMobile} sectionTitle="리뷰 분석">
          <Stack
            gap={isMobile ? '20px' : '34px'}
            width={'100%'}
            // minWidth={isMobile ? '100%' : '500px'}
            alignItems={'center'}
          >
            <AppStorePostsBarChart currentAppStore={currentAppStore} isMobile={isMobile} />
            <PostsWordsSummaries currentAppStore={currentAppStore} isMobile={isMobile} />
          </Stack>
        </DashboardSectionContainer>
        {isMobile ? (
          ''
        ) : (
          <DashboardSectionContainer isMobile={isMobile} sectionTitle="최근 작성된 리뷰">
            <LatestReviews currentAppStore={currentAppStore} />
          </DashboardSectionContainer>
        )}
      </FlexBox>
      {showNoticeModal ? (
        <GoogleChannelRequestModal
          showModal={showNoticeModal}
          handleShowButton={() => {
            setShowRequestGoogleButton(!showRequestGoogleButton);
          }}
          handleShowModal={() => {
            setShowNoticeModal(!showNoticeModal);
            clearInterval(polling);
          }}
          updateCheckStatus={() => setCheckGooGleCrawlingStatus(!checkGooGleCrawlingStatus)}
          appStoreId={currentAppStore.storeId}
        />
      ) : (
        ''
      )}
      {showGoogleStatusModal && googleRequestStatus !== undefined ? (
        <GoogleChannelRequestStatusModal
          showModal={showGoogleStatusModal}
          handleShowModal={() => setShowGoogleStatusModal(!showGoogleStatusModal)}
          onCloseModal={() => {
            setCheckGooGleCrawlingStatus(false);
          }}
          status={googleRequestStatus}
        />
      ) : (
        ''
      )}
    </>
  );
};

export default BaseDashboardContents;
